import React from 'react'
import Svg, { Circle, Path } from 'react-native-svg'

import { colors } from '../../tokens'

/**
 * SVG glyphs for the picker components, rendered **directly** (not through the
 * universal `Icon` wrapper) with an explicit `color` passed straight to `fill`
 * or `stroke`. Two earlier approaches each broke on React Native:
 * - the vendored `@gluestack-ui` icons through `Icon` → the wrapper forces
 *   `fill`, so `react-native-svg` painted the whole glyph solid (a calendar as a
 *   filled square, a clock as a filled disc);
 * - `stroke='currentColor'` through `Icon` → web inherits the colour via CSS,
 *   but `react-native-svg` has no `currentColor` source from the wrapper, so the
 *   stroke fell back to black.
 *
 * Passing the colour straight to the paint attribute sidesteps both: glyphs
 * render identically on web and mobile. Internal only (not exported from the
 * barrel).
 */

export const GLYPH_COLORS = {
    default: colors.content.default,
    muted: colors.content.muted,
    accent: colors.action.default,
}

export type GlyphProps = {
    /** Square size in px. */
    size?: number
    /** Stroke colour (hex). Defaults to the standard text colour. */
    color?: string
}

const STROKE = {
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
}

const resolve = (color?: string): string => color ?? GLYPH_COLORS.default

// The four chevrons render the same filled, square-ended geometry as the
// compiled DS icon set (icons/line/ChevronLeftIcon.tsx and its Right/Up/Down siblings),
// drawn directly rather than through the universal Icon wrapper for the same
// React Native reasons documented above. Previously these were round-capped
// strokes, a different and softer icon family right next to the same-shaped
// filled glyphs the DS uses everywhere else, per the rounded-icons defect.
// The down arrow doubles as PickerField's chevron-down trigger and
// TimePicker's decrement button, so it has to match its up/left/right
// siblings inside this same picker family, not just the DS in isolation.
export const ChevronLeftGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
        <Path
            fill={resolve(color)}
            fillRule='evenodd'
            clipRule='evenodd'
            d='M15.798 8.298L11.574 12.5L15.798 16.702L14.5 18L9 12.5L14.5 7L15.798 8.298Z'
        />
    </Svg>
)

export const ChevronRightGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
        <Path
            fill={resolve(color)}
            fillRule='evenodd'
            clipRule='evenodd'
            d='M9.0001 16.702L13.2241 12.5L9.00009 8.298L10.2981 7L15.7981 12.5L10.2981 18L9.0001 16.702Z'
        />
    </Svg>
)

export const ChevronUpGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
        <Path
            fill={resolve(color)}
            fillRule='evenodd'
            clipRule='evenodd'
            d='M15.803 14.899L11.601 10.675L7.39895 14.899L6.10095 13.601L11.601 8.10101L17.101 13.601L15.803 14.899Z'
        />
    </Svg>
)

export const ChevronDownGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => (
    <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
        <Path
            fill={resolve(color)}
            fillRule='evenodd'
            clipRule='evenodd'
            d='M8.19693 9.10099L12.3989 13.325L16.6009 9.10099L17.8989 10.399L12.3989 15.899L6.89893 10.399L8.19693 9.10099Z'
        />
    </Svg>
)

// AOS-977: filled calendar with a solid header band + a row of three day-dots,
// exact vector from Figma `DatePickerCalendar` (node 46:1381), viewBox 17×20.
// The `-3 -2` viewBox origin insets the 17×20 glyph inside a 24×24 box (left 3,
// top 2, right 4, bottom 2) exactly as Figma places it. `evenodd` fill turns the
// body window and the three dots into the see-through / marked areas.
const CALENDAR_PATH =
    'M15.096 18.0288V6.99519H1.904V18.0288H15.096ZM15.096 1.97115C15.6098 1.97115 16.0556 2.17147 16.4333 2.57212C16.8111 2.97276 17 3.44551 17 3.99038V18.0288C17 18.5737 16.8111 19.0385 16.4333 19.4231C16.0556 19.8077 15.6098 20 15.096 20H1.904C1.37511 20 0.925558 19.8077 0.555333 19.4231C0.185109 19.0385 0 18.5737 0 18.0288V3.99038C0 3.44551 0.185109 2.97276 0.555333 2.57212C0.925558 2.17147 1.37511 1.97115 1.904 1.97115H2.83333V0H4.73733V1.97115H12.2627V0H14.1667V1.97115H15.096ZM13.2373 9.01442V10.9856H11.3333V9.01442H13.2373ZM9.42933 9.01442V10.9856H7.57067V9.01442H9.42933ZM5.66667 9.01442V10.9856H3.76267V9.01442H5.66667Z'

export const CalendarGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => (
    <Svg width={size} height={size} viewBox='-3 -2 24 24' fill='none'>
        <Path d={CALENDAR_PATH} fill={resolve(color)} fillRule='evenodd' clipRule='evenodd' />
    </Svg>
)

export const ClockGlyph: React.FC<GlyphProps> = ({ size = 24, color }) => {
    const stroke = resolve(color)
    return (
        <Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
            <Circle cx={12} cy={12} r={9} stroke={stroke} {...STROKE} />
            <Path d='M12 7v5l3.5 2' stroke={stroke} {...STROKE} />
        </Svg>
    )
}
